import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components'
import Camera from './components/Pages/Camera'
import Settings from './components/Pages/Settings';
import WECs from './components/Pages/WECs';
import { useMQTTConnect } from './hooks/mqtt';
import { getMqttCredentials } from './server';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  align-items: center;
  text-align: center;
  gap: 3vmax;
  padding: 17vh 0;
  max-width: 1200px;
  margin: 0 auto;
`

const ConnectionStatus = styled.h3`
  position: absolute;
  top: 2rem;
  color: gray;
`

const Tabs = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  gap: 8px;
`

const Tab = styled.button`
  display: inline-block;
  background-color: transparent;
  border: none;
  color: #529fce;
  width: 8em;
  height: 3em;
  border-radius: 99px;
  font-size: 1em;
  font-weight: bold;
  font-family: inherit;
  cursor: pointer;
  ${(props) => props.selected && `
    background-color: #529fce;
    cursor: default;
    color: white;
  `}
`

export default function Home({ wecs, updateWec }) {
  const [activeTab, setActiveTab] = useState('WECs')
  const [messages, setMessages] = useState({})
  const [credentails, setCredentials] = useState(null)

  useEffect(() => {
    (async () => {
      try {
        const creds = await getMqttCredentials()
        setCredentials(creds)
      } catch (e) {
        console.error("Failed to get MQTT credentials:", e)
      }
    })()
  }, [])

  const onMessage = useCallback((topic, data) => {
    // console.log(`Received message on ${topic}:`, data)
    const [, hostname, message_type, sensor_id] = topic.split('/')

    if (!Array.isArray(data)) {
      data = data == null ? [] : [data];
    }

    for (const dataPoint of data) {
      setMessages((messages) => {
        const sensor = sensor_id || dataPoint.sensor_id || message_type

        if (dataPoint.timestamp && messages[hostname]?.[message_type]?.[sensor]?.timestamp) {
          if (new Date(dataPoint.timestamp) < new Date(messages[hostname][message_type][sensor].timestamp)) {
            console.log("Ignoring data point because it's older than existing data point")
            return messages
          }
        }

        // Add data frequency for data points with timestamp
        if (dataPoint.timestamp) {
          const existingDataPoint = messages[hostname]?.[message_type]?.[sensor]
          if (existingDataPoint && existingDataPoint.timestamp) {
            const timeDiff = new Date(dataPoint.timestamp) - new Date(existingDataPoint.timestamp)
            dataPoint.frequency = timeDiff > 0 ? 1000 / timeDiff : null
          }
        }

        return {
          ...messages,
          [hostname]: {
            ...messages[hostname],
            [message_type]: {
              ...messages[hostname]?.[message_type],
              [sensor]: dataPoint
            }
          }
        }
      })
    }
  }, [])
  
  const { client: mqttClient, status: mqttStatus } = useMQTTConnect(credentails, onMessage)

  // Online WECs first, then the most recently seen ones. Only the MQTT
  // status messages know who is online, so the API sort can't do this.
  const sortedWecs = useMemo(() => {
    const isOnline = (wec) => messages[wec.hostname]?.status?.status?.connected === true

    return wecs && [...wecs].sort((a, b) => {
      if (isOnline(a) !== isOnline(b))
        return isOnline(a) ? -1 : 1

      return (b.last_online || '').localeCompare(a.last_online || '')
    })
  }, [wecs, messages])

  return (
    <Container>
      {mqttStatus !== "Connected" && <ConnectionStatus>{mqttStatus}</ConnectionStatus>}
      <Tabs>
        <Tab selected={activeTab === 'WECs'} onClick={() => setActiveTab('WECs')}>WECs</Tab>
        <Tab selected={activeTab === 'Camera'} onClick={() => setActiveTab('Camera')}>Camera</Tab>
        <Tab selected={activeTab === 'Settings'} onClick={() => setActiveTab('Settings')}>Settings</Tab>
      </Tabs>
      {activeTab === 'WECs' && sortedWecs?.map(wec => <WECs
        key={wec.id || wec.hostname}
        wec={wec}
        updateWec={updateWec}
        messages={messages[wec.hostname] || []}
      />)}
      {activeTab === 'Camera' && <Camera mqttClient={mqttClient} wecs={sortedWecs} messages={messages} />}
      {activeTab === 'Settings' && <Settings mqttClient={mqttClient} wecs={sortedWecs} messages={messages} />}
    </Container>
  )
}